import { useQueryClient, useMutation } from "@tanstack/react-query"
import { fetcher } from "../fetcher"
import { storeValue } from "../../utils"
import { useNavigation } from "@react-navigation/native"
import { NativeStackNavigationProp } from "@react-navigation/native-stack"
import { RootStackParamList } from "../../router/types.ts"
import { getSearchParams } from "../../utils/searchParams"

type MutationFnParams = {
  tokenId: string
  publicToken: string
  linkAccountsMetadata: {
    linkSessionId: string
    institutionId?: string
    accountIds: string[]
  }
  useAccessToken?: boolean
}

type AccountLinkResponse = {
  refreshToken: string
  isOk: boolean
  errors: unknown[]
}

export function useAccountLink() {
  const queryClient = useQueryClient()
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList, keyof RootStackParamList>>()

  return useMutation<AccountLinkResponse, unknown, MutationFnParams>({
    mutationFn: async ({ tokenId, publicToken, linkAccountsMetadata, useAccessToken = false }) => {
      const searchParams = getSearchParams()
      const appendix = searchParams.get("token") ? `?authLink=${searchParams.get("token")}` : ""
      const response = await fetcher(
        `/Account/Link${appendix}`,
        {
          method: "PUT",
          body: JSON.stringify({
            tokenId,
            publicToken,
            linkAccountsMetadata,
          }),
        },
        {
          useAccessToken,
          disableErrorScreen: true,
        }
      )

      const responseJson = await response.json()

      if (!responseJson.isOk) {
        throw new Error("Could not link accounts")
      }

      return responseJson
    },
    onSuccess: (response) => {
      if (response.refreshToken) {
        storeValue("refreshToken", response.refreshToken)
      }
      queryClient.invalidateQueries({ queryKey: ["accountList"] })
      queryClient.setQueryData(["AccountLink"], response)
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        try {
          const parsedError = JSON.parse(error.message)
          const displayMessage = parsedError.linkError?.displayMessage || parsedError.error.Message
          navigation.navigate("BankConnectionError", { message: displayMessage })

          return
        } catch (e) {
          let message
          if (e instanceof Error) message = e.message
          else message = String(e)
          throw new Error(message)
        }
      }

      let message
      if (error instanceof Error) message = error.message
      else message = String(error)

      navigation.navigate("BankConnectionError", { message })
    },
  })
}
